import './ClientConfig.scss';
import { Dispatch, ReactElement, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { MdRefresh } from 'react-icons/md';
import { COMMERCE_OPS } from '@blisspointmedia/bpm-types/dist/CommerceOps';
import {
  COMMERCE,
  PROGRAMMATIC,
  SEARCHSHOPPING,
  SOCIAL,
  STREAMING_PLUS,
  YOUTUBE
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { EMAIL } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Intent, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Page } from '@Components/Page';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import {
  getInvalidSalesforceIds,
  isValidSalesforceId,
  isValidSlackChannel,
  normalizeSlackChannel,
  SALESFORCE_ID_LENGTH
} from './client-config-utils';
import { awaitJSON, DagLambdaFetch, SheetsLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';

const AGENCY = 'tinuiti';
const DISPLAY_TIMEZONES = ['UTC', 'Pacific', 'Mountain', 'Central', 'Eastern'];
const LOWERCASE_REGEXP = /[a-z]/;
const NUMBERS_REGEX = /[0-9]/;
const PATH_REGEXP =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b\/([-a-zA-Z0-9@:%_+.~#()?&//=])/;
const PROTOCOL_REGEXP = /http(s)?:\/\/[-a-zA-Z0-9@:%._+~#=]?/;
const SPECIAL_CHARACTERS = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
const UPPERCASE_REGEXP = /[A-Z]/;
const VALID_URL_REGEXP =
  /^http(s)?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#()?&//=]*)/;

const MEDIA_TYPES = [
  { label: 'Audio', value: STREAMING_PLUS[1] },
  { label: 'Commerce', value: COMMERCE },
  { label: 'Commerce Ops', value: COMMERCE_OPS },
  { label: 'Display', value: STREAMING_PLUS[2] },
  { label: 'Linear', value: 'tv' },
  { label: 'Radio', value: 'radio' },
  { label: 'Search & Shopping', value: SEARCHSHOPPING },
  { label: 'Social', value: SOCIAL },
  { label: 'Streaming', value: STREAMING_PLUS[0] },
  { label: 'YouTube', value: YOUTUBE },
  { label: 'Programmatic', value: PROGRAMMATIC },
  { label: 'Email', value: EMAIL }
];

const IAB_CATEGORY_MAP = {
  IAB1: 'IAB1 Arts & Entertainment',
  IAB2: 'IAB2 Automotive',
  IAB3: 'IAB3 Business',
  IAB4: 'IAB4 Careers',
  IAB5: 'IAB5 Education',
  IAB6: 'IAB6 Family & Parenting',
  IAB7: 'IAB7 Health & Fitness',
  IAB8: 'IAB8 Food & Drink',
  IAB9: 'IAB9 Hobbies & Interests',
  IAB10: 'IAB10 Home & Garden',
  IAB11: "IAB11 Law, Gov't & Politics",
  IAB12: 'IAB12 News',
  IAB13: 'IAB13 Personal Finance',
  IAB14: 'IAB14 Society',
  IAB15: 'IAB15 Science',
  IAB16: 'IAB16 Pets',
  IAB17: 'IAB17 Sports',
  IAB18: 'IAB18 Style & Fashion',
  IAB19: 'IAB19 Technology & Computing',
  IAB20: 'IAB20 Travel',
  IAB21: 'IAB21 Real Estate',
  IAB22: 'IAB22 Shopping',
  IAB23: 'IAB23 Religion & Spirituality',
  IAB24: 'IAB24 Uncategorized'
};
const IAB_CATEGORY_OPTIONS = Object.entries(IAB_CATEGORY_MAP).map(([value, label]) => ({
  value,
  label
}));

// Companies with stale/bad data can have a beeswax_iab_category that isn't one of our IAB1-24
// codes (e.g. leftover test values). Rather than pass that straight to the Dropdown — which,
// unlike bpm-frontend's old DropdownButton, falls back to displaying the raw unmatched value
// instead of going blank — fall back to the Uncategorized bucket for anything unrecognized.
const resolveIABCategory = (value: string | undefined): string =>
  value && value in IAB_CATEGORY_MAP ? value : 'IAB24';

interface ClientConfigParams {
  agency: string;
  beeswaxDefaultClick: string;
  beeswaxDomain: string;
  beeswaxIABCategory: string;
  companyId: string;
  companyName: string;
  companyNameAbbreviation: string;
  internalOnlyMediaTypes: Record<string, boolean>;
  kpiTimezone: string;
  kpiTimezoneDisplay: string;
  mediaTypes: string[];
  salesforceIDs: string[];
  slackChannel: string;
  streamingTypes: string[];
  pacingAccess: boolean;
}

interface EditClientBody {
  company: string;
  mediaTypes: string[];
  salesforceIDs: string[];
  pacingAccess: boolean;
  // Omitted leaves the channel unchanged; blank clears it.
  slackChannel?: string;
}

// Resolves a media type's current on/off value: prefer the in-progress edit map, falling back to
// the value the company started with, and finally to off. Shared by the "Enabled" and "Internal
// Only" checkboxes below (with different map/initialMap pairs), since both follow this same
// override-then-initial-then-default lookup.
const resolveMediaTypeValue = (
  map: Record<string, boolean> | undefined,
  initialMap: Record<string, boolean>,
  key: string
): boolean => {
  if (map && map[key] !== undefined) return map[key];
  if (initialMap[key] !== undefined) return initialMap[key];
  return false;
};

interface MediaTypeRowProps {
  label: string;
  value: string;
  mediaMap?: Record<string, boolean>;
  initialMediaMap: Record<string, boolean>;
  setMediaMap: Dispatch<SetStateAction<Record<string, boolean> | undefined>>;
  internalOnlyMap: Record<string, boolean>;
  initialInternalOnlyMap: Record<string, boolean>;
  setInternalOnlyMap: Dispatch<SetStateAction<Record<string, boolean>>>;
}

const MediaTypeRow = ({
  label,
  value,
  mediaMap,
  initialMediaMap,
  setMediaMap,
  internalOnlyMap,
  initialInternalOnlyMap,
  setInternalOnlyMap
}: MediaTypeRowProps) => {
  const mediaTypeEnabled = resolveMediaTypeValue(mediaMap, initialMediaMap, value);

  return (
    <div className='media-type-container'>
      <div className='label'>{label}</div>
      <div className='checkbox-container'>
        <Checkbox
          checked={mediaTypeEnabled}
          onChange={() =>
            setMediaMap(map => ({
              ...map,
              [value]: !resolveMediaTypeValue(mediaMap, initialMediaMap, value)
            }))
          }
        />
        <div className='checkbox-label'>Enabled</div>
      </div>

      <div className='checkbox-container'>
        <Checkbox
          checked={resolveMediaTypeValue(internalOnlyMap, initialInternalOnlyMap, value)}
          onChange={() =>
            setInternalOnlyMap(map => ({
              ...map,
              [value]: !resolveMediaTypeValue(internalOnlyMap, initialInternalOnlyMap, value)
            }))
          }
          disabled={!mediaTypeEnabled}
        />
        <div className='checkbox-label'>Internal Only</div>
      </div>
    </div>
  );
};

const getKpiTimezoneValue = (kpiTimezoneDisplay: string): string => {
  if (kpiTimezoneDisplay === 'Eastern') {
    return 'America/New_York';
  }
  if (kpiTimezoneDisplay === 'UTC') {
    return 'Etc/UTC';
  }
  if (kpiTimezoneDisplay === 'Central') {
    return 'America/Chicago';
  }
  if (kpiTimezoneDisplay === 'Mountain') {
    return 'America/Denver';
  }
  if (kpiTimezoneDisplay === 'Pacific') {
    return 'America/Los_Angeles';
  }
  return 'invalid';
};

// Serves both the global "Add New Client" page (/add-new-client, no company in the URL) and the
// per-company "Edit Client" page (/:company/edit-client) from a single component, mirroring
// bpm-frontend's existingClient-prop split. tinuiti-frontend's route config has no mechanism to
// pass arbitrary props to a pageComponent, so the mode is derived from the URL itself instead.
export const ClientConfig = (): ReactElement => {
  const existingClient = Boolean(useLocationCompany());
  // Params
  const [company, setCompany] = useState<string>('');
  const [companyAbbreviation, setCompanyAbbreviation] = useState<string>('');
  const [companyDomain, setCompanyDomain] = useState<string>('');
  const [companyHomePage, setCompanyHomePage] = useState<string>('');
  const [companyID, setCompanyID] = useState<string>('');
  const [slackChannel, setSlackChannel] = useState<string>('');
  const [initialSlackChannel, setInitialSlackChannel] = useState<string>('');
  const [iabCategory, setIABCategory] = useState<string>('IAB24'); // Default to IAB24
  const [kpiTimezone, setKpiTimezone] = useState<string>(
    DISPLAY_TIMEZONES[DISPLAY_TIMEZONES.length - 1] // Default to Eastern
  );
  const [pacingAccess, setPacingAccess] = useState<boolean>(false);
  const [mediaMap, setMediaMap] = useState<Record<string, boolean>>();
  const [internalOnlyMap, setInternalOnlyMap] = useState<Record<string, boolean>>({});
  // ------
  const [initialMediaMap, setInitialMediaMap] = useState<Record<string, boolean>>({});
  const [initialInternalOnlyMap, setInitialInternalOnlyMap] = useState<Record<string, boolean>>({});
  const [options, setOptions] = useState<string[]>();
  const [refreshing, setRefreshing] = useState(false);
  const [salesforceIDs, setSalesforceIDs] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const companyInfo = useCompanyInfo();
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();
  const useCompanyOptions = (): string[] => {
    useEffect(() => {
      if (!options || !options.length) {
        (async () => {
          try {
            const res = await SheetsLambdaFetch('/get_company_ids');
            const jsonResponse = await awaitJSON<{ ids: string[] }>(res);
            setOptions(jsonResponse.ids);
            setRefreshing(false);
          } catch (e) {
            setError({
              title: 'Internal Error',
              message: 'Could not fetch existing company ids',
              reportError: e
            });
            setOptions([]);
          }
        })();
      }
    });
    return options || [];
  };
  const companyOptions = useCompanyOptions();

  const saveChanges = async () => {
    setSaving(false);
    const message = `Successfully ${existingClient ? 'updated' : 'added'} ${company}.`;
    const finalMediaMap: Record<string, boolean> = mediaMap ?? {};
    const finalInternalOnlyMap: Record<string, boolean> = internalOnlyMap ?? {};
    let isNoMediaTypeEnabled = true;
    for (const mediaType of Object.keys(finalMediaMap)) {
      if (finalMediaMap[mediaType]) {
        isNoMediaTypeEnabled = false;
        break;
      }
    }
    if (isNoMediaTypeEnabled) {
      setError({
        message: 'Please select at least one channel',
        title: 'Error: Channels'
      });
      return;
    }
    if (Object.keys(salesforceIDs).length === 0) {
      setError({
        message: 'Please enter at least one Salesforce ID',
        title: 'Error: Salesforce IDs'
      });
      return;
    }
    const invalidSalesforceIDs = getInvalidSalesforceIds(Object.keys(salesforceIDs));
    if (invalidSalesforceIDs.length > 0) {
      setError({
        message: `Salesforce IDs must be exactly ${SALESFORCE_ID_LENGTH} characters. Invalid: ${invalidSalesforceIDs.join(', ')}`,
        title: 'Error: Salesforce IDs'
      });
      return;
    }
    if (!URL.canParse(companyDomain)) {
      setError({ message: `Company Domain (${companyDomain}) is invalid.` });
      return;
    }
    if (!URL.canParse(companyHomePage)) {
      setError({ message: `Company Homepage (${companyHomePage}) is invalid.` });
      return;
    }
    if (!isValidSlackChannel(slackChannel)) {
      setError({
        message: `Slack Channel (${slackChannel}) is invalid. Use lowercase letters, numbers, hyphens and underscores, e.g. #team-companyname.`,
        title: 'Error: Slack Channel'
      });
      return;
    }
    if (existingClient) {
      try {
        setSaving(true);
        await setAreYouSure({
          title: 'Please review the information below',
          message: reviewInformation(),
          okayText: existingClient ? 'Edit Client' : 'Add New Client'
        });
        try {
          const body = {
            company: companyInfo.cid,
            mediaTypes: Object.keys(finalMediaMap).filter(
              mediaType => finalMediaMap[mediaType] === true
            ),
            internalOnlyMediaTypes: finalInternalOnlyMap,
            beeswaxDomain: companyDomain,
            beeswaxDefaultClick: companyHomePage,
            salesforceIDs: Object.keys(salesforceIDs),
            pacingAccess,
            // Only sent when edited, so a stale company-info cache that lacks the channel can't blank it.
            ...(normalizeSlackChannel(slackChannel) !== initialSlackChannel
              ? { slackChannel: normalizeSlackChannel(slackChannel) }
              : {})
          };
          await DagLambdaFetch<EditClientBody>('/edit_client', {
            method: 'POST',
            body
          });
          await setError({
            intent: Intent.SUCCESS,
            title: 'Success!',
            message
          });
          setSaving(false);
        } catch (e) {
          setSaving(false);
          setError({ title: 'Internal Error', message: 'Client was not updated', reportError: e });
        }
      } catch {
        setSaving(false);
      }
    } else {
      const streamingTypes: string[] = [];
      const mediaTypes: string[] = [];

      if (!company) {
        setError({ message: 'Please enter a company name', title: 'Error: Company Name' });
        return;
      }

      if (!companyID) {
        setError({ message: 'Please enter a company id', title: 'Error: Company ID' });
        return;
      }
      if (SPECIAL_CHARACTERS.test(companyID)) {
        setError({
          message: 'Company id cannot contain spaces or special characters',
          title: 'Error: Company ID'
        });
        return;
      }
      if (UPPERCASE_REGEXP.test(companyID)) {
        setError({
          message: 'Company id must contain only lowercase letters',
          title: 'Error: Company ID'
        });
        return;
      }

      if (!companyAbbreviation) {
        setError({
          message: 'Please enter a company name abbreviation',
          title: 'Error: Company Name Abbreviation'
        });
        return;
      }
      if (companyAbbreviation.length !== 2) {
        setError({
          message: 'Company name abbreviation must be two letters',
          title: 'Error: Company Name Abbreviation'
        });
        return;
      }
      if (
        SPECIAL_CHARACTERS.test(companyAbbreviation) ||
        LOWERCASE_REGEXP.test(companyAbbreviation) ||
        NUMBERS_REGEX.test(companyAbbreviation)
      ) {
        setError({
          message: 'Company name abbreviation can only contain uppercase letters',
          title: 'Error: Company Name Abbreviation'
        });
        return;
      }

      if (!companyDomain) {
        setError({
          message: 'Please enter the company domain ',
          title: 'Error: Company Domain'
        });
        return;
      }
      if (!PROTOCOL_REGEXP.test(companyDomain)) {
        setError({
          message: 'Please begin with the url protocol https://',
          title: 'Error: Company Domain'
        });
        return;
      }
      if (PATH_REGEXP.test(companyDomain) || companyDomain[companyDomain.length - 1] === '/') {
        setError({
          message:
            'Please do not include any characters following the top level domain (ie nothing after .com)',
          title: 'Error: Company Domain'
        });
        return;
      }

      if (!VALID_URL_REGEXP.test(companyDomain)) {
        setError({
          message: 'Company domain not valid',
          title: 'Error: Company Domain'
        });
        return;
      }

      if (!companyHomePage) {
        setError({
          message: 'Please enter the company home page',
          title: 'Error: Company Home Page'
        });
        return;
      }
      if (!PROTOCOL_REGEXP.test(companyHomePage)) {
        setError({
          message: 'Please begin with the url protocol https://',
          title: 'Error: Company Home Page'
        });
        return;
      }
      if (companyHomePage[companyHomePage.length - 1] === '/') {
        setError({
          message: 'Home Page cannot end in with a / character',
          title: 'Error: Company Domain'
        });
        return;
      }
      if (!VALID_URL_REGEXP.test(companyHomePage)) {
        setError({
          message: 'Company home page url not valid',
          title: 'Error: Company Home Page'
        });
        return;
      }

      for (const type of Object.keys(finalMediaMap)) {
        if (finalMediaMap[type] !== undefined) {
          if (
            ['audio', 'streaming'].includes(type) &&
            !streamingTypes.includes(type.toLowerCase())
          ) {
            streamingTypes.push(type.toLowerCase());
          }
          if (!mediaTypes.includes(type.toLowerCase())) {
            mediaTypes.push(type.toLowerCase());
          }
        }
      }

      try {
        setSaving(true);
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const jsonResponse = await awaitJSON<{ ids: string[] }>(res);
          if (jsonResponse.ids.includes(companyID)) {
            setError({
              message: `The company id ${companyID} already exists. If you are unable to see ${companyID}, you may not have permissions to view or it may be disabled. If this a new company, please enter a different id.`,
              title: 'Error: Company ID'
            });
            setSaving(false);
            return;
          }
        } catch (e) {
          setSaving(false);
          setError({
            title: 'Internal Error',
            message: 'Could not fetch existing company ids',
            reportError: e
          });
        }
        await setAreYouSure({
          title: 'Please review the information below',
          message: reviewInformation(),
          okayText: existingClient ? 'Edit Client' : 'Add New Client'
        });
        try {
          setKpiTimezone('');

          const params: ClientConfigParams = {
            agency: AGENCY,
            beeswaxDefaultClick: companyHomePage,
            beeswaxDomain: companyDomain,
            beeswaxIABCategory: iabCategory,
            companyId: companyID,
            companyName: company,
            companyNameAbbreviation: companyAbbreviation,
            kpiTimezone: getKpiTimezoneValue(kpiTimezone),
            kpiTimezoneDisplay: kpiTimezone,
            mediaTypes,
            internalOnlyMediaTypes: finalInternalOnlyMap,
            streamingTypes,
            salesforceIDs: Object.keys(salesforceIDs),
            slackChannel: normalizeSlackChannel(slackChannel),
            pacingAccess
          };

          await SheetsLambdaFetch<{ params: ClientConfigParams }>('/add_new_client', {
            method: 'POST',
            body: { params }
          });
          await setError({
            intent: Intent.SUCCESS,
            title: 'Success!',
            message: 'Successfully added client'
          });
          setSaving(false);
          window.location.reload();
        } catch (e) {
          setSaving(false);
          setError({ title: 'Internal Error', message: 'Client was not added', reportError: e });
        }
      } catch {
        setSaving(false);
      }
    }
  };

  const reviewInformation = (): string => {
    const finalMediaMap: Record<string, boolean> = mediaMap ?? {};
    let message = `Company Name: ${company}
    Company ID: ${companyID}
    Company Name Abbreviation: ${companyAbbreviation}
    Agency: ${AGENCY}
    IAB Category: ${IAB_CATEGORY_MAP[iabCategory]}
    Company Domain: ${companyDomain}
    Company Home Page: ${companyHomePage}
    Slack Channel: ${normalizeSlackChannel(slackChannel) || 'None'}
    Pacing Access: ${pacingAccess ? 'Client-Facing' : 'Internal Only'}
    Channels:`;
    for (const mediaType of Object.keys(finalMediaMap)) {
      if (finalMediaMap[mediaType] === true) {
        const capitalizedMediaType =
          mediaType === 'tv'
            ? 'Linear'
            : `${mediaType.charAt(0).toUpperCase()}${mediaType.slice(1)}`;
        message = `${message}\n${capitalizedMediaType}`;
      }
    }
    return message;
  };

  useEffect(() => {
    const newInitialMediaMap: Record<string, boolean> = {};
    const newInitialInternalOnlyMap: Record<string, boolean> = {};
    if (existingClient && companyInfo) {
      // add internal only media types to company info fetch
      const { media_types, page_access_settings, salesforce_ids, pacing_client_facing } =
        companyInfo;
      const mediaTypes = media_types ?? [];
      const pageAccessSettings = page_access_settings ?? {};
      const pacingClientFacing = pacing_client_facing ?? false;
      if (mediaMap === undefined) {
        for (const mediaType of mediaTypes) {
          newInitialMediaMap[mediaType] = true;

          if (pageAccessSettings[mediaType] && pageAccessSettings[mediaType] === 'internal') {
            newInitialInternalOnlyMap[mediaType] = true;
          }
        }

        setInitialMediaMap(newInitialMediaMap);
        setMediaMap(newInitialMediaMap);
        setInitialInternalOnlyMap(newInitialInternalOnlyMap);
        setInternalOnlyMap(newInitialInternalOnlyMap);
        setCompany(companyInfo.name);
        setCompanyAbbreviation(companyInfo.name_abbrev ?? '');
        setCompanyDomain(companyInfo.beeswax_advertiser_domain ?? '');
        setCompanyHomePage(companyInfo.beeswax_default_click ?? '');
        setCompanyID(companyInfo.cid ?? '');
        const existingSlackChannel =
          (companyInfo as { slack_channel?: string | null }).slack_channel ?? '';
        setSlackChannel(existingSlackChannel);
        setInitialSlackChannel(existingSlackChannel);
        setIABCategory(resolveIABCategory(companyInfo.beeswax_iab_category));
        const newSalesforceIDs: Record<string, boolean> = {};
        for (const id of salesforce_ids ?? []) {
          newSalesforceIDs[id] = true;
        }
        setSalesforceIDs(newSalesforceIDs);
        setPacingAccess(pacingClientFacing);
      }
    } else if (mediaMap === undefined) {
      setInitialMediaMap(newInitialMediaMap);
      setMediaMap(newInitialMediaMap);
    }
  }, [companyInfo, existingClient, mediaMap]);

  const optionList = useMemo(
    () => [
      { label: 'Add New Client ', value: 'new' },
      ...companyOptions.map(value => ({ value, label: value }))
    ],
    [companyOptions]
  );

  return (
    <Page
      title={existingClient ? 'Edit Client' : 'Add New Client'}
      pageType={existingClient ? 'Edit Client' : 'Add New Client'}
      actions={
        <div className='add-new-client-actions'>
          <Dropdown
            size={Size.LG}
            className='select'
            searchable

            placeholder='Search for existing companies...'
            options={optionList}
            onChange={value => {
              if (!value) return;
              // Canonical hyphenated paths, not the legacy bpm-frontend underscore spelling —
              // the underscore redirect for edit-client (a per-company route) drops the company
              // segment: location-utils.tsx's buildRoutes redirects via `<Navigate to={fullPath}>`,
              // and fullPath for a Client-type location has no company segment baked in (it's
              // implicit via the parent :company route), so React Router treats the absolute-
              // looking `to` as rooted at "/" and escapes the :company nesting entirely.
              window.location.href =
                value === 'new'
                  ? `${window.location.origin}/add-new-client`
                  : `${window.location.origin}/${value}/edit-client`;
            }}
          />
          {!existingClient && (
            <IconButton
              className='refresh'
              loading={refreshing}
              onClick={() => {
                setOptions(undefined);
                setRefreshing(true);
              }}
            >
              <MdRefresh />
            </IconButton>
          )}
        </div>
      }
    >
      <div className='add-new-client-body'>
        <Form className='form'>
          <div className='company-info'>
            <Form.Group as={Col} className='company-name'>
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                size='lg'
                type='text'
                value={company}
                disabled={existingClient}
                placeholder='eg. Company Name'
                onChange={e => {
                  setCompany(e.currentTarget.value);
                }}
              />
            </Form.Group>

            <Form.Group as={Col} className='company-id'>
              <Form.Label>Company ID</Form.Label>
              <Form.Control
                size='lg'
                type='text'
                value={companyID}
                disabled={existingClient}
                placeholder='eg. companyname'
                onChange={e => {
                  setCompanyID(e.currentTarget.value);
                }}
              />
            </Form.Group>
          </div>
          <div className='websites'>
            <Form.Group as={Col} className='company-domain'>
              <Form.Label>Company Domain</Form.Label>
              <Form.Control
                size='lg'
                type='url'
                value={companyDomain}
                placeholder='https://www.companyname.com'
                onChange={e => {
                  setCompanyDomain(e.currentTarget.value);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} className='company-home-page'>
              <Form.Label>Company Home Page</Form.Label>
              <Form.Control
                size='lg'
                type='url'
                value={companyHomePage}
                placeholder='https://www.companyname.com/us'
                onChange={e => {
                  setCompanyHomePage(e.currentTarget.value);
                }}
              />
            </Form.Group>
          </div>
          <div className='websites'>
            <Form.Group as={Col} className='company-slack-channel'>
              <Form.Label>Client Slack Channel</Form.Label>
              <Form.Control
                size='lg'
                type='text'
                value={slackChannel}
                isInvalid={!isValidSlackChannel(slackChannel)}
                placeholder='#team-companyname'
                onChange={e => {
                  setSlackChannel(e.currentTarget.value);
                }}
              />
              <Form.Text>Where alerts for this client are posted. Leave blank for none.</Form.Text>
            </Form.Group>
          </div>
          <div className='row2'>
            <Form.Group as={Col} className='company-abbreviation'>
              <Form.Label>Company Name Abbreviation</Form.Label>
              <Form.Control
                size='lg'
                maxLength={2}
                value={companyAbbreviation}
                disabled={existingClient}
                placeholder='eg. CN'
                onChange={e => {
                  setCompanyAbbreviation(e.currentTarget.value);
                }}
              />
              <Form.Text>Required, two uppercase letters only</Form.Text>
            </Form.Group>
            <Form.Group className='iab-category'>
              <Form.Label>IAB Category</Form.Label>
              <Dropdown
                size={Size.LG}
                stretch={StretchMode.FIT}
                options={IAB_CATEGORY_OPTIONS}
                value={iabCategory}
                disabled={existingClient}
                onChange={setIABCategory}
              />
            </Form.Group>
          </div>
          <div className='pacing'>
            <Form.Group>
              <Form.Label>Pacing</Form.Label>
              <ToggleSwitch
                label='Client-Facing'
                size={Size.LG}
                checked={pacingAccess}
                onChange={setPacingAccess}
              />
            </Form.Group>
          </div>
          {mediaMap && (
            <div className='row3'>
              <Form.Group as={Col} className='salesforce-ids'>
                <Form.Label>Salesforce IDs</Form.Label>
                <Dropdown
                  size={Size.LG}
                  searchable
                  creatable
                  multiSelect
                  showChips
                  clearable
                  hideCaret
                  ariaLabel='Salesforce IDs'
                  placeholder='Enter IDs!'
                  value={Object.keys(salesforceIDs)}
                  onChange={ids => setSalesforceIDs(Object.fromEntries(ids.map(id => [id, true])))}
                  reactSelectProps={{
                    // The menu search keeps its text after a pick, so skip IDs already added.
                    isValidNewOption: input =>
                      isValidSalesforceId(input.trim()) &&
                      !Object.hasOwn(salesforceIDs, input.trim()),
                    getNewOptionData: input => ({ label: input.trim(), value: input.trim() }),
                    noOptionsMessage: ({ inputValue }) =>
                      Object.hasOwn(salesforceIDs, inputValue.trim())
                        ? 'This Salesforce ID is already added'
                        : `Salesforce IDs must be exactly ${SALESFORCE_ID_LENGTH} alphanumeric characters`
                  }}
                />
              </Form.Group>
              {!existingClient && (
                <Form.Group as={Col} className='kpi-timezone'>
                  <Form.Label>KPI Timezone</Form.Label>
                  <Dropdown
                    size={Size.LG}
                    stretch={StretchMode.FIT}
                    options={DISPLAY_TIMEZONES}
                    value={kpiTimezone}
                    onChange={setKpiTimezone}
                  />
                </Form.Group>
              )}
              <Form.Group as={Col} className='campaign-types'>
                <div className='campaign-types-header'>
                  <Form.Label>Channels</Form.Label>
                  <InfoTooltip rightLabel='More info' size={Size.MD}>
                    Set which media types are enabled for this client. &quot;Internal Only&quot;
                    means that its single-channel page will only be visible to internal users in the
                    nav bar.
                  </InfoTooltip>
                </div>
                <div className='media-types'>
                  {MEDIA_TYPES.map(({ label, value }) => (
                    <MediaTypeRow
                      key={value}
                      label={label}
                      value={value}
                      mediaMap={mediaMap}
                      initialMediaMap={initialMediaMap}
                      setMediaMap={setMediaMap}
                      internalOnlyMap={internalOnlyMap}
                      initialInternalOnlyMap={initialInternalOnlyMap}
                      setInternalOnlyMap={setInternalOnlyMap}
                    />
                  ))}
                </div>
              </Form.Group>
            </div>
          )}
          <Form.Group as={Col} className='save-button'>
            <Button
              size={Size.LG}
              design={Intent.SUCCESS}
              loading={saving}
              onClick={() => saveChanges()}
            >
              {existingClient ? 'Edit Client' : 'Add New Client'}
            </Button>
          </Form.Group>
        </Form>
      </div>
    </Page>
  );
};
